// ExportToWord.js
import React from "react";
import { saveAs } from "file-saver";
import {
  Document, Packer, Paragraph, Table, TableCell, TableRow,
  TextRun, WidthType, AlignmentType, VerticalAlign
} from "docx";
import * as RiIcons from 'react-icons/ri';
import { confirmAlert } from 'react-confirm-alert';

export const ExportToWord = ({ apiData, fileName }) => {

  const columnWidths = [500, 1500, 1100, 1100, 1100, 1100, 1100, 1100];

  const combineWidths = (indexes) => indexes.reduce((sum, i) => sum + columnWidths[i], 0);

  const createCell = (space, text, width, bold = false, borders = {}) =>
    new TableCell({
      width: { size: width, type: WidthType.DXA },
      borders,
      children: [
        new Paragraph({
          alignment: AlignmentType.LEFT,
          spacing: { before: space, after: space },
          children: [new TextRun({ text: String(text), bold, }),],
        })
      ],
    });

  const createMergedCell = (space, text, colSpan, indexes, align = AlignmentType.CENTER) =>
    new TableCell({
      columnSpan: colSpan,
      width: { size: combineWidths(indexes), type: WidthType.DXA },
      verticalAlign: VerticalAlign.BOTTOM,
      borders: {
        top: { style: "single", size: 1, color: "000000" },
        left: { style: "single", size: 1, color: "000000" },
        right: { style: "single", size: 1, color: "000000" },
        bottom: { style: "single", size: 1, color: "000000" }
      },
      children: [
        new Paragraph({
          alignment: align,
          spacing: { before: space, after: space },
          children: [
            new TextRun({ text, bold: true })
          ]
        })
      ]
    });

  const generateDoc = async () => {
    const [first] = apiData || [{}];
    const { hospitalname = "", hospitalcode = "", monthname = "" } = first;

    const totals = ['totalbirths', 'sncu', 'nicu', 'nbsu', 'totaltags', 'iuddata']
      .reduce((acc, key) => ({ ...acc, [key]: apiData.reduce((sum, item) => sum + (item[key] || 0), 0) }), {});

    const tableRows = [
      new TableRow({ children: [createMergedCell(300, "INFANT PROTECTION SYSTEM MONTHLY REPORT", 8, [0, 1, 2, 3, 4, 5, 6, 7])] }),
      new TableRow({ children: [createMergedCell(100, `Name of the Facility : ${hospitalname}`, 8, [0, 1, 2, 3, 4, 5, 6, 7], AlignmentType.LEFT)] }),
      new TableRow({ children: [createMergedCell(100, `Fot the Date of : ${monthname}`, 8, [0, 1, 2, 3, 4, 5, 6, 7], AlignmentType.LEFT)] }),
      new TableRow({ children: [createMergedCell(100, `Name of Superintendent/HOD : Dr`, 8, [0, 1, 2, 3, 4, 5, 6, 7], AlignmentType.LEFT)] }),

      new TableRow({
        children: [
          new TableCell({ columnSpan: 3, width: { size: combineWidths([0, 1, 2]), type: WidthType.DXA }, borders: {}, children: [new Paragraph("")] }),
          new TableCell({
            columnSpan: 3,
            width: {
              size: combineWidths([3, 4, 5]),
              type: WidthType.DXA
            },
            borders: {},
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 100, after: 100 },
                children: [
                  new TextRun({
                    text: "Outbound Entries",
                    bold: true,
                  }),
                ],
              }
            )]
          }),
          new TableCell({ columnSpan: 2, width: { size: combineWidths([6, 7]), type: WidthType.DXA }, borders: {}, children: [new Paragraph("")] }),
        ]
      }),

      new TableRow({
        children: [
          ...["S No", "Delivery Date", "Total Births", "SNCU", "NICU", "NBSU", "Total Tags Attached (INFANT)", "IUD"]
            .map((title, i) => createCell(100, title, columnWidths[i], true))
        ]
      }),

      ...apiData.map((item, i) => new TableRow({
        children: [
          createCell(0, i + 1, columnWidths[0], false),
          createCell(0, item.date || "", columnWidths[1], false),
          createCell(0, item.totalbirths || "-", columnWidths[2], false),
          createCell(0, item.sncu || "-", columnWidths[3], false),
          createCell(0, item.nicu || "-", columnWidths[4], false),
          createCell(0, item.nbsu || "-", columnWidths[5], false),
          createCell(0, item.totaltags || "-", columnWidths[6], false),
          createCell(0, item.iuddata || "-", columnWidths[7], false),
        ]
      })),

      new TableRow({
        children: [
          createCell(100, "", columnWidths[0], true),
          createCell(100, "", columnWidths[1], true),
          createCell(100, `Total: ${totals.totalbirths}`, columnWidths[2], true),
          createCell(100, `Total: ${totals.sncu}`, columnWidths[3], true),
          createCell(100, `Total: ${totals.nicu}`, columnWidths[4], true),
          createCell(100, `Total: ${totals.nbsu}`, columnWidths[5], true),
          createCell(100, `Total: ${totals.totaltags}`, columnWidths[6], true),
          createCell(100, `Total: ${totals.iuddata}`, columnWidths[7], true),
        ]
      }),

      new TableRow({
        children: [
          new TableCell({
            columnSpan: 2,
            width: { size: combineWidths([0, 1]), type: WidthType.DXA },
            verticalAlign: VerticalAlign.BOTTOM,
            borders: { top: { style: "single", size: 1, color: "000000" }, right: { style: "single", size: 1, color: "000000" } },
            children: [new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 500, after: 200 },
              children: [
                new TextRun({ text: "Signature of the Employee", bold: true })
              ]
            })]
          }),
          new TableCell({
            columnSpan: 3,
            width: { size: combineWidths([2, 3, 4]), type: WidthType.DXA },
            verticalAlign: VerticalAlign.BOTTOM,
            borders: { top: { style: "single", size: 1, color: "000000" }, right: { style: "single", size: 1, color: "000000" } },
            children: [new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 500, after: 200 },
              children: [
                new TextRun({ text: `Signature of the Nursing Superintendent ${hospitalcode}`, bold: true })
              ]
            })]
          }),
          new TableCell({
            columnSpan: 3,
            width: { size: combineWidths([5, 6, 7]), type: WidthType.DXA },
            verticalAlign: VerticalAlign.BOTTOM,
            borders: { top: { style: "single", size: 1, color: "000000" }, right: { style: "single", size: 1, color: "000000" } },
            children: [new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 500, after: 200 },
              children: [
                new TextRun({ text: `Signature of the Superintendent ${hospitalcode}`, bold: true })
              ]
            })]
          })
        ]
      })
    ];

    const doc = new Document({
      sections: [{ children: [new Paragraph(" "), new Table({ rows: tableRows })] }]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${fileName}.docx`);
  };

  const handleExport = () => {
    confirmAlert({
      title: "Confirm Export",
      message: "Are you sure you want to export this data to Word format?",
      buttons: [
        { label: "Yes", onClick: generateDoc },
        { label: "No" }
      ]
    });
  };

  return (
    <RiIcons.RiFileWord2Fill style={{ marginRight: "20px", color: "#1E88E5", height: "20px", width: "20px" }}
      onClick={handleExport} />
  );
};


